import { Link } from "react-router-dom"
import { Globe, Twitter, Github, Linkedin, Calendar } from "lucide-react"
import { UserAvatar } from "./UserAvatar"
import { FollowButton } from "./FollowButton"
import { Badge } from "@/components/ui/badge"
import { formatDate, formatNumber } from "@/lib/utils"

// Helper to extract date from Firestore Timestamp or ISO string
function getDate(value: unknown): Date {
  if (!value) return new Date()
  if (typeof value === "string") return new Date(value)
  if (value instanceof Date) return value
  if (typeof value === "object" && "toDate" in value && typeof (value as { toDate: () => Date }).toDate === "function") {
    return (value as { toDate: () => Date }).toDate()
  }
  return new Date()
}

interface UserProfileProps {
  user: {
    uid: string
    displayName?: string
    username?: string
    avatarUrl?: string
    bio?: string
    role?: "user" | "admin" | "moderator"
    followerCount: number
    followingCount: number
    postCount: number
    reputation: number
    website?: string
    twitter?: string
    github?: string
    linkedin?: string
    createdAt?: unknown
  }
  currentUserId?: string
  isFollowing?: boolean
}

export function UserProfile({ user, currentUserId, isFollowing = false }: UserProfileProps) {
  const isOwnProfile = currentUserId === user.uid

  return (
    <div className="relative">
      {/* Background banner */}
      <div className="h-32 bg-gradient-to-r from-indigo-500 to-indigo-600 rounded-t-lg" />

      {/* Profile content */}
      <div className="relative px-6 pb-6">
        {/* Avatar */}
        <div className="absolute -top-12">
          <div className="rounded-full border-4 border-white dark:border-gray-950">
            <UserAvatar src={user.avatarUrl} name={user.displayName || "User"} size="xl" />
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end pt-4">
          {isOwnProfile ? (
            <Link to="/settings">
              <Badge variant="outline">Edit Profile</Badge>
            </Link>
          ) : (
            currentUserId && <FollowButton userId={user.uid} currentUserId={currentUserId} initialFollowing={isFollowing} />
          )}
        </div>

        {/* User info */}
        <div className="mt-8">
          <div className="flex items-center space-x-2">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{user.displayName}</h1>
            {user.role === "admin" && <Badge>Admin</Badge>}
            {user.role === "moderator" && <Badge variant="secondary">Moderator</Badge>}
          </div>
          <p className="text-gray-500 dark:text-gray-400">@{user.username}</p>

          {user.bio && <p className="mt-4 text-gray-700 dark:text-gray-300 max-w-2xl">{user.bio}</p>}

          {/* Stats */}
          <div className="mt-4 flex flex-wrap gap-6 text-sm">
            <span className="text-gray-600 dark:text-gray-400">
              <strong className="text-gray-900 dark:text-white">{formatNumber(user.followerCount)}</strong> followers
            </span>
            <span className="text-gray-600 dark:text-gray-400">
              <strong className="text-gray-900 dark:text-white">{formatNumber(user.followingCount)}</strong> following
            </span>
            <span className="text-gray-600 dark:text-gray-400">
              <strong className="text-gray-900 dark:text-white">{formatNumber(user.postCount)}</strong> posts
            </span>
            <span className="text-gray-600 dark:text-gray-400">
              <strong className="text-gray-900 dark:text-white">{formatNumber(user.reputation)}</strong> reputation
            </span>
          </div>

          {/* Links & join date */}
          <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
            {user.website && (
              <a
                href={user.website}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center hover:text-indigo-600 dark:hover:text-indigo-400"
              >
                <Globe className="mr-1 h-4 w-4" />
                Website
              </a>
            )}
            {user.twitter && (
              <a
                href={`https://twitter.com/${user.twitter}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center hover:text-indigo-600 dark:hover:text-indigo-400"
              >
                <Twitter className="mr-1 h-4 w-4" />@{user.twitter}
              </a>
            )}
            {user.github && (
              <a
                href={`https://github.com/${user.github}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center hover:text-indigo-600 dark:hover:text-indigo-400"
              >
                <Github className="mr-1 h-4 w-4" />
                {user.github}
              </a>
            )}
            {user.linkedin && (
              <a
                href={`https://linkedin.com/in/${user.linkedin}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center hover:text-indigo-600 dark:hover:text-indigo-400"
              >
                <Linkedin className="mr-1 h-4 w-4" />
                LinkedIn
              </a>
            )}
            <span className="flex items-center">
              <Calendar className="mr-1 h-4 w-4" />
              Joined {formatDate(getDate(user.createdAt))}
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}
